import { startAuthentication } from '@simplewebauthn/browser';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowRight, Bookmark, Globe, KeyRound, Server, Shield } from 'lucide-react';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type { SessionState } from '@harness/shared';
import { GithubIcon, Logo, Spinner } from '../components/ui';
import { api, errorText } from '../lib/api';
import { qk } from '../lib/keys';
import { toast } from '../lib/store';

type Mode = 'login' | 'setup' | 'twofa' | 'reset' | 'forgot';

export default function AuthScreen({ mode: initial }: { mode: Mode }) {
  const [mode, setMode] = useState<Mode>(initial);
  useEffect(() => setMode(initial), [initial]);
  return (
    <div className="auth">
      <div className="auth-hero">
        <div className="auth-brand">
          <Logo height={30} />
          <span className="topbar-sep" />
          <span className="topbar-name">AI Harness</span>
        </div>
        <div className="auth-hero-body">
          <h1 className="auth-headline">
            Every project.
            <br />
            <span>Its own agent.</span>
          </h1>
          <p className="auth-sub">An agent per project that remembers the project, works on its server, and pushes nothing without your approval.</p>
          <div className="auth-tiles">
            <div className="tile tile-lavender">
              <span className="icon-chip" style={{ color: 'var(--tile-lavender-ink)' }}>
                <Bookmark className="icon" />
              </span>
              <span className="t">Remembers</span>
              <span className="d">Memory per project, shared by its agents</span>
            </div>
            <div className="tile tile-mint">
              <span className="icon-chip" style={{ color: 'var(--tile-mint-ink)' }}>
                <Server className="icon" />
              </span>
              <span className="t">Works on your server</span>
              <span className="d">Reads, edits and runs code where it lives</span>
            </div>
            <div className="tile tile-cream">
              <span className="icon-chip" style={{ color: 'var(--tile-cream-ink)' }}>
                <Shield className="icon" />
              </span>
              <span className="t">Asks first</span>
              <span className="d">Pushes and ticket updates wait for you</span>
            </div>
          </div>
        </div>
      </div>
      <div className="auth-panel">
        <div className="card auth-card">
          {mode === 'login' && <Login onForgot={() => setMode('forgot')} />}
          {mode === 'setup' && <Setup />}
          {mode === 'twofa' && <TwoFactor />}
          {mode === 'forgot' && <Forgot onBack={() => setMode('login')} />}
          {mode === 'reset' && <Reset />}
        </div>
      </div>
    </div>
  );
}

function Title({ title, sub }: { title: string; sub: string }) {
  return (
    <>
      <div>
        <h2>{title}</h2>
        <div className="muted">{sub}</div>
      </div>
    </>
  );
}

function useSessionState() {
  const qc = useQueryClient();
  return qc.getQueryData<SessionState>(qk.session);
}

const OAUTH_PROVIDERS = [
  { id: 'github', label: 'GitHub', icon: <GithubIcon style={{ fontSize: 15 }} /> },
  { id: 'google', label: 'Google', icon: <Globe className="icon" style={{ fontSize: 15 }} /> },
  { id: 'oidc', label: 'Single sign-on (OIDC)', icon: <Shield className="icon" style={{ fontSize: 15 }} /> },
] as const;

function OAuthButtons() {
  const s = useSessionState();
  const go = (p: 'github' | 'google' | 'oidc') => {
    if (!s?.oauth[p]) {
      toast(`${p === 'oidc' ? 'OIDC' : p === 'github' ? 'GitHub' : 'Google'} sign-in isn’t configured on this harness. Set its client ID and secret in the server environment.`, 'error');
      return;
    }
    window.location.href = `/api/auth/oauth/${p}/start`;
  };
  // Only providers configured in the server environment are offered.
  const enabled = OAUTH_PROVIDERS.filter((p) => s?.oauth[p.id]);
  if (enabled.length === 0) return null;
  return (
    <>
      <div className="or-rule">
        <span />
        or
        <span />
      </div>
      <div className="oauth-grid">
        {enabled.map((p) => (
          <button
            key={p.id}
            className="btn btn-secondary"
            type="button"
            onClick={() => go(p.id)}
            style={enabled.length === 1 || (enabled.length === 3 && p.id === 'oidc') ? { gridColumn: 'span 2' } : undefined}
          >
            {p.icon}
            {p.label}
          </button>
        ))}
      </div>
    </>
  );
}

function Login({ onForgot }: { onForgot: () => void }) {
  const qc = useQueryClient();
  const s = useSessionState();
  const [params] = useSearchParams();
  const [email, setEmail] = useState('');
  const [pw, setPw] = useState('');
  const [err, setErr] = useState(params.get('error') ?? '');
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!pw) return setErr('Enter your password.');
    setBusy(true);
    try {
      await api.post('/api/auth/login', { email, password: pw });
      await qc.invalidateQueries({ queryKey: qk.session });
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  // SSO-only harness (PASSWORD_LOGIN=false): just the provider buttons.
  if (s && !s.passwordLogin) {
    const anyOAuth = Object.values(s.oauth).some(Boolean);
    return (
      <div className="auth-form">
        <Title title="Sign in" sub={anyOAuth ? 'This harness signs in through your identity provider.' : 'No sign-in methods are configured on this harness.'} />
        {err && <div className="err" role="alert">{err}</div>}
        <OAuthButtons />
      </div>
    );
  }
  return (
    <form className="auth-form" onSubmit={submit}>
      <Title title="Sign in" sub="Use your account or your organisation's SSO." />
      <div className="field">
        <label htmlFor="email">Email</label>
        <input id="email" className="input" type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} autoFocus />
      </div>
      <div className="field">
        <label htmlFor="pw">Password</label>
        <input id="pw" className="input" type="password" autoComplete="current-password" value={pw} placeholder="••••••••••" onChange={(e) => (setPw(e.target.value), setErr(''))} />
      </div>
      {err && <div className="err" role="alert">{err}</div>}
      <button className="btn btn-primary btn-lg btn-block" type="submit" disabled={busy}>
        Sign in
        {busy ? <Spinner /> : <ArrowRight className="icon" style={{ fontSize: 16 }} />}
      </button>
      <OAuthButtons />
      <a
        href="#"
        onClick={(e) => {
          e.preventDefault();
          onForgot();
        }}
        style={{ fontSize: 13, alignSelf: 'flex-start' }}
      >
        Forgot password?
      </a>
    </form>
  );
}

function Setup() {
  const qc = useQueryClient();
  const [f, setF] = useState({ name: '', email: '', password: '' });
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (f.password.length < 10) return setErr('Use at least 10 characters for the password.');
    setBusy(true);
    try {
      await api.post('/api/auth/setup', f);
      await qc.invalidateQueries({ queryKey: qk.session });
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form className="auth-form" onSubmit={submit}>
      <Title title="Create the owner account" sub="First run. This account owns every project on this harness." />
      <div className="field">
        <label htmlFor="name">Name</label>
        <input id="name" className="input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} autoFocus required />
      </div>
      <div className="field">
        <label htmlFor="email">Email</label>
        <input id="email" className="input" type="email" autoComplete="username" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} required />
      </div>
      <div className="field">
        <label htmlFor="pw">Password</label>
        <input id="pw" className="input" type="password" autoComplete="new-password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} required />
        <div className="hint">At least 10 characters. You’ll set up two-step verification before connecting any server.</div>
      </div>
      {err && <div className="err" role="alert">{err}</div>}
      <button className="btn btn-primary btn-lg btn-block" type="submit" disabled={busy}>
        Create account
        {busy ? <Spinner /> : <ArrowRight className="icon" style={{ fontSize: 16 }} />}
      </button>
      {/* First sign-in can also happen through a configured provider (JIT-provisions the owner). */}
      <OAuthButtons />
    </form>
  );
}

function TwoFactor() {
  const qc = useQueryClient();
  const s = useSessionState();
  const [code, setCode] = useState('');
  const [recovery, setRecovery] = useState<string | null>(null);
  const [verifying, setVerifying] = useState(false);
  const [err, setErr] = useState('');
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => ref.current?.focus(), [recovery]);

  const finish = async () => {
    await qc.invalidateQueries({ queryKey: qk.session });
  };
  const verify = async (body: { code?: string; recoveryCode?: string }) => {
    setVerifying(true);
    setErr('');
    try {
      await api.post('/api/auth/2fa/verify', body);
      await finish();
    } catch (e) {
      setErr(errorText(e));
      setCode('');
    } finally {
      setVerifying(false);
    }
  };
  const onCode = (v: string) => {
    const d = v.replace(/\D/g, '').slice(0, 6);
    setCode(d);
    setErr('');
    if (d.length === 6) void verify({ code: d });
  };
  const passkey = async () => {
    setErr('');
    try {
      const optionsJSON = await api.post<Parameters<typeof startAuthentication>[0]['optionsJSON']>('/api/auth/passkey/options');
      const response = await startAuthentication({ optionsJSON });
      setVerifying(true);
      await api.post('/api/auth/passkey/verify', { response });
      await finish();
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setVerifying(false);
    }
  };
  const back = async () => {
    await api.post('/api/auth/logout');
    await finish();
  };

  return (
    <div className="auth-form">
      <span className="success-icon" aria-hidden>
        <KeyRound className="icon" />
      </span>
      <Title title="Two-step verification" sub={recovery !== null ? 'Enter one of the recovery codes you saved when you set up two-step verification.' : `Enter the 6-digit code from your authenticator app for ${s?.email ?? 'your account'}.`} />
      {recovery === null ? (
        <div className="field">
          <label htmlFor="code">Authentication code</label>
          <input
            id="code"
            ref={ref}
            className="input otp"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={code}
            onChange={(e) => onCode(e.target.value)}
            placeholder="000000"
            disabled={verifying}
          />
        </div>
      ) : (
        <form
          className="field"
          onSubmit={(e) => {
            e.preventDefault();
            void verify({ recoveryCode: recovery });
          }}
        >
          <label htmlFor="rec">Recovery code</label>
          <input id="rec" ref={ref} className="input mono" value={recovery} onChange={(e) => setRecovery(e.target.value)} placeholder="xxxxx-xxxxx" />
        </form>
      )}
      {verifying && (
        <div className="muted" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <Spinner />
          Verifying…
        </div>
      )}
      {err && <div className="err" role="alert">{err}</div>}
      {recovery !== null ? (
        <button className="btn btn-primary btn-lg btn-block" onClick={() => void verify({ recoveryCode: recovery })} disabled={verifying || recovery.length < 6}>
          Verify recovery code
          <ArrowRight className="icon" style={{ fontSize: 16 }} />
        </button>
      ) : (
        <button className="btn btn-primary btn-lg btn-block" onClick={passkey} disabled={verifying}>
          Use a passkey instead
          <KeyRound className="icon" style={{ fontSize: 16 }} />
        </button>
      )}
      <div style={{ display: 'flex', gap: 16, fontSize: 13 }}>
        <a
          href="#"
          onClick={(e) => {
            e.preventDefault();
            setErr('');
            setRecovery(recovery === null ? '' : null);
          }}
        >
          {recovery === null ? 'Use a recovery code' : 'Use an authenticator code'}
        </a>
        <a
          href="#"
          onClick={(e) => {
            e.preventDefault();
            void back();
          }}
          style={{ color: 'var(--color-neutral-700)' }}
        >
          Back
        </a>
      </div>
    </div>
  );
}

function Forgot({ onBack }: { onBack: () => void }) {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post('/api/auth/forgot', { email });
      setSent(true);
    } catch (e) {
      toast(errorText(e), 'error');
    } finally {
      setBusy(false);
    }
  };
  return (
    <form className="auth-form" onSubmit={submit}>
      <Title title="Reset password" sub="We’ll email a single-use link that works for 30 minutes." />
      {sent ? (
        <div className="status-line">If an account uses {email}, a reset link is on its way.</div>
      ) : (
        <>
          <div className="field">
            <label htmlFor="email">Email</label>
            <input id="email" className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoFocus required />
          </div>
          <button className="btn btn-primary btn-lg btn-block" type="submit" disabled={busy}>
            Send reset link
            <ArrowRight className="icon" style={{ fontSize: 16 }} />
          </button>
        </>
      )}
      <a
        href="#"
        onClick={(e) => {
          e.preventDefault();
          onBack();
        }}
        style={{ fontSize: 13, color: 'var(--color-neutral-700)' }}
      >
        Back to sign in
      </a>
    </form>
  );
}

function Reset() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [pw, setPw] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post('/api/auth/reset', { token: params.get('token') ?? '', password: pw });
      toast('Password changed · sign in with the new one');
      nav('/login', { replace: true });
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form className="auth-form" onSubmit={submit}>
      <Title title="Choose a new password" sub="Every signed-in session will be signed out." />
      <div className="field">
        <label htmlFor="pw">New password</label>
        <input id="pw" className="input" type="password" autoComplete="new-password" value={pw} onChange={(e) => setPw(e.target.value)} autoFocus minLength={10} required />
      </div>
      {err && <div className="err" role="alert">{err}</div>}
      <button className="btn btn-primary btn-lg btn-block" type="submit" disabled={busy}>
        Set password
        <ArrowRight className="icon" style={{ fontSize: 16 }} />
      </button>
    </form>
  );
}
